import { getBankStatementMonthKey, type BankStatementPeriodLike } from "./bankHealthDateFilter";
import { resolveCanonicalBankMoneyOut } from "@shared/bankMoneyOut";

export interface BankHealthMetricStatement extends BankStatementPeriodLike {
  closingBalance?: string | null;
  totalCredits?: string | null;
  statedCredits?: string | null;
  totalDebits?: string | null;
  statedDebits?: string | null;
  checksAmount?: string | null;
  serviceFees?: string | null;
  moneyOutOverride?: string | null;
  dealerCorrectedCredits?: string | null;
  dealerCorrectedDebits?: string | null;
  dealerVerifiedAt?: string | Date | null;
}

export interface BankHealthHeadlineMetrics {
  cashOnHand: number | null;
  monthlyBurnRate: number;
  cashFlowRatio: number | null;
  daysOfCash: number | null;
  totalCredits: number;
  totalDebits: number;
  monthCount: number;
  latestMonthKey: string | null;
}

export function parseBankMetricNumber(value: string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function effectiveBankCredits(statement: BankHealthMetricStatement): number | null {
  return parseBankMetricNumber(
    statement.dealerVerifiedAt
      ? statement.dealerCorrectedCredits
      : statement.statedCredits ?? statement.totalCredits,
  );
}

export function effectiveBankMoneyOut(statement: BankHealthMetricStatement): number | null {
  return resolveCanonicalBankMoneyOut(statement)?.value ?? null;
}

/**
 * Canonical Bank Health headline aggregation. Callers must pass only statements
 * whose summary is visible (Tier 1 or Tier 2), after applying their surface's
 * selected date scope.
 */
export function computeBankHealthHeadlineMetrics(
  statements: readonly BankHealthMetricStatement[],
): BankHealthHeadlineMetrics | null {
  if (statements.length === 0) return null;

  const totalCredits = statements.reduce(
    (sum, statement) => sum + (effectiveBankCredits(statement) ?? 0),
    0,
  );
  const resolvedMoneyOut = statements.map(effectiveBankMoneyOut);
  if (resolvedMoneyOut.some((value) => value == null)) return null;
  const totalDebits = resolvedMoneyOut.reduce<number>((sum, value) => sum + value!, 0);
  const monthKeys = new Set(
    statements
      .map((statement) => getBankStatementMonthKey(statement))
      .filter((key): key is string => key != null),
  );
  const monthCount = Math.max(monthKeys.size, 1);
  const monthlyBurnRate = totalDebits / monthCount;
  const latestMonthKey = [...monthKeys].sort().pop() ?? null;
  const latestMonthClosings = latestMonthKey
    ? statements
        .filter((statement) => getBankStatementMonthKey(statement) === latestMonthKey)
        .map((statement) => parseBankMetricNumber(statement.closingBalance))
        .filter((value): value is number => value != null)
    : [];
  const cashOnHand = latestMonthClosings.length > 0
    ? latestMonthClosings.reduce((sum, value) => sum + value, 0)
    : null;

  return {
    cashOnHand,
    monthlyBurnRate,
    cashFlowRatio: totalDebits > 0 ? totalCredits / totalDebits : null,
    daysOfCash: cashOnHand != null && monthlyBurnRate > 0
      ? (cashOnHand / monthlyBurnRate) * 30
      : null,
    totalCredits,
    totalDebits,
    monthCount,
    latestMonthKey,
  };
}